import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Languages, ShieldCheck, Crown, Building2, GraduationCap, Wallet,
  BookOpen, Users, User, Bus, Briefcase, ShoppingBag
} from "lucide-react";

// Demo credentials — all roles available for quick-login showcase.
// Backend auto-seeds these on startup (see app_settings.disable_demo_seed).
// SECURITY: In production, set REACT_APP_SHOW_DEMO_LOGINS=false to hide the
// quick-login grid AND to seed a random admin email/password default in the form.
const SHOW_DEMO_LOGINS = process.env.REACT_APP_SHOW_DEMO_LOGINS !== "false";
const DEMO_ACCOUNTS = SHOW_DEMO_LOGINS ? [
  { role: "super_admin",  email: "admin@school.sa",       password: "Admin@2026",    icon: Crown,         labelAr: "المدير العام",   labelEn: "Super Admin" },
  { role: "school_admin", email: "schooladmin@school.sa", password: "School@2026",   icon: Building2,     labelAr: "مدير المدرسة",   labelEn: "School Admin" },
  { role: "academic",     email: "academic@school.sa",    password: "Academic@2026", icon: GraduationCap, labelAr: "الشؤون الأكاديمية", labelEn: "Academic" },
  { role: "hr",           email: "hr@school.sa",          password: "Hr@2026",       icon: Briefcase,     labelAr: "الموارد البشرية", labelEn: "HR" },
  { role: "finance",      email: "finance@school.sa",     password: "Finance@2026",  icon: Wallet,        labelAr: "المالية",        labelEn: "Finance" },
  { role: "teacher",      email: "teacher@school.sa",     password: "Teacher@2026",  icon: BookOpen,      labelAr: "معلّم",          labelEn: "Teacher" },
  { role: "parent",       email: "parent@school.sa",      password: "Parent@2026",   icon: Users,         labelAr: "وليّ أمر",       labelEn: "Parent" },
  { role: "student",      email: "student@school.sa",     password: "Student@2026",  icon: User,          labelAr: "طالب",           labelEn: "Student" },
  { role: "driver",       email: "driver@school.sa",      password: "Driver@2026",   icon: Bus,           labelAr: "سائق",           labelEn: "Driver" },
  { role: "canteen_staff",email: "canteen@school.sa",     password: "Canteen@2026",  icon: ShoppingBag,   labelAr: "موظف المقصف",    labelEn: "Canteen Staff" },
] : [];

export default function LoginPage() {
  const { user, login, loginWithSession, error } = useAuth();
  const { t, lang, setLang, langs } = useI18n();
  const loc = useLocation();
  const [mode, setMode] = useState("staff");
  const [studentCode, setStudentCode] = useState("");
  const [studentErr, setStudentErr] = useState("");
  const [email, setEmail] = useState(SHOW_DEMO_LOGINS ? "admin@school.sa" : "");
  const [password, setPassword] = useState(SHOW_DEMO_LOGINS ? "Admin@2026" : "");
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message || "تم إرسال الرابط إن كان البريد مسجلاً");
      setForgotOpen(false); setForgotEmail("");
    } catch {
      toast.error("تعذر إرسال الطلب — حاول مجدداً");
    } finally { setForgotBusy(false); }
  };

  if (user && user !== false) return <Navigate to={loc.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  const studentSubmit = async (e) => {
    e.preventDefault();
    setStudentErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/student-login", { code: studentCode.trim() });
      loginWithSession(data);
    } catch (err) {
      setStudentErr(err.response?.data?.detail || "رمز الدخول غير صحيح");
    } finally { setBusy(false); }
  };

  const quickLogin = async (acct) => {
    setDemoBusy(acct.role);
    setEmail(acct.email);
    setPassword(acct.password);
    await login(acct.email, acct.password);
    setDemoBusy(null);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758611228434-7b5b697abd0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzY2hvb2wlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3ODQzNjg4M3ww&ixlib=rb-4.1.0&q=85"
          alt="School"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 paper-bg opacity-10" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur grid place-items-center text-sm font-bold">SI</div>
            <div>
              <div className="text-lg font-semibold">{t("appName")}</div>
              <div className="text-xs opacity-80">{t("tagline")}</div>
            </div>
          </div>
          <div className="space-y-4 max-w-md">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              {{
                ar: "إدارة مدارس متكاملة، بهوية عربية حديثة.",
                en: "Holistic school management, with a modern identity.",
                sv: "Komplett skolhantering med en modern identitet.",
                da: "Komplet skoleadministration med en moderne identitet.",
              }[lang]}
            </h1>
            <p className="text-sm opacity-90 leading-relaxed">
              {{
                ar: "حضور، درجات، سلوك، رسوم، واجبات، رسائل — كل شيء في مكان واحد.",
                en: "Attendance, grades, behavior, fees, homework, messages — all in one place.",
                sv: "Närvaro, betyg, beteende, avgifter, läxor, meddelanden — allt på ett ställe.",
                da: "Fremmøde, karakterer, adfærd, afgifter, lektier, beskeder — alt samlet ét sted.",
              }[lang]}
            </p>
          </div>
          <div className="text-xs opacity-70">© {new Date().getFullYear()} Madrasati</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-8 border border-border shadow-none bg-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-primary" />
              {t("login")}
            </div>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-auto h-8 gap-2 px-2 text-xs" data-testid="login-lang-select">
                <Languages className="w-3.5 h-3.5" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {langs.map(l => <SelectItem key={l.code} value={l.code} data-testid={`login-lang-${l.code}`}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold mb-1">{t("welcome")}</h2>
          <p className="text-sm text-muted-foreground mb-4">{t("loginCta")}</p>

          {/* Mode toggle: staff/parent (email+password) vs student (single code) */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-muted/50 rounded-xl mb-5" data-testid="login-mode-toggle">
            <button type="button" onClick={() => setMode("staff")}
              className={`h-9 rounded-lg text-sm font-medium transition-all ${mode === "staff" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="login-mode-staff">
              {{ ar: "الموظفون وأولياء الأمور", en: "Staff & Parents", sv: "Personal", da: "Personale" }[lang]}
            </button>
            <button type="button" onClick={() => setMode("student")}
              className={`h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${mode === "student" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}
              data-testid="login-mode-student">
              <GraduationCap className="w-4 h-4" />{{ ar: "دخول الطلاب", en: "Student", sv: "Elev", da: "Elev" }[lang]}
            </button>
          </div>

          {mode === "student" ? (
            <form onSubmit={studentSubmit} className="space-y-4" data-testid="student-login-form">
              <div>
                <Label htmlFor="scode">{{ ar: "رمز الطالب", en: "Student code", sv: "Elevkod", da: "Elevkode" }[lang]}</Label>
                <Input id="scode" value={studentCode}
                  onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                  required placeholder="ABC123" dir="ltr"
                  className="text-center text-2xl tracking-[0.3em] font-mono h-14 uppercase"
                  data-testid="student-code-input" autoComplete="off" maxLength={10} />
                <p className="text-xs text-muted-foreground mt-2">
                  {{ ar: "أدخل الرمز المكوّن من حروف وأرقام الموجود في ملفك الشخصي (يُدار من المدرسة).",
                     en: "Enter the code from your profile (managed by your school).",
                     sv: "Ange koden från din profil.", da: "Indtast koden fra din profil." }[lang]}
                </p>
              </div>
              {studentErr && <div className="text-sm text-destructive" data-testid="student-login-error">{studentErr}</div>}
              <Button type="submit" disabled={busy} className="w-full h-11" data-testid="student-login-submit">
                {busy ? "..." : { ar: "دخول", en: "Enter", sv: "Logga in", da: "Log ind" }[lang]}
              </Button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email" autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password" autoComplete="current-password"
              />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11" data-testid="login-submit">
              {busy ? "..." : t("loginCta")}
            </Button>
            <button type="button" onClick={() => setForgotOpen(true)}
              className="block mx-auto text-xs text-muted-foreground hover:text-primary underline"
              data-testid="forgot-password-link">
              {{ ar: "نسيت كلمة المرور؟", en: "Forgot password?", sv: "Glömt lösenord?", da: "Glemt adgangskode?" }[lang]}
            </button>
          </form>
          )}

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent className="max-w-sm" dir={lang === "ar" ? "rtl" : "ltr"}>
              <DialogHeader><DialogTitle>{lang === "ar" ? "إعادة تعيين كلمة المرور" : "Reset password"}</DialogTitle></DialogHeader>
              <form onSubmit={sendForgot} className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {lang === "ar" ? "أدخل بريدك الإلكتروني وسنرسل لك رابطاً لتعيين كلمة مرور جديدة." : "Enter your email and we'll send a reset link."}
                </p>
                <Input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                  placeholder="email@school.sa" data-testid="forgot-email-input" />
                <Button type="submit" disabled={forgotBusy} className="w-full" data-testid="forgot-submit">
                  {forgotBusy ? "..." : (lang === "ar" ? "إرسال الرابط" : "Send link")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <div className="mt-6 border-t border-border pt-4" hidden={!SHOW_DEMO_LOGINS}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium text-foreground">
                {{ ar: "تسجيل دخول سريع — حسابات تجريبية", en: "Quick demo login", sv: "Snabb demo-inloggning", da: "Hurtig demo-login" }[lang]}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {{ ar: "اضغط دور لدخول فوري", en: "Tap a role to sign in", sv: "Tryck för att logga in", da: "Tryk for at logge ind" }[lang]}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="demo-roles-grid">
              {DEMO_ACCOUNTS.map((acct) => {
                const Icon = acct.icon;
                const label = lang === "ar" ? acct.labelAr : acct.labelEn;
                const isBusy = demoBusy === acct.role;
                return (
                  <button
                    key={acct.role}
                    type="button"
                    onClick={() => quickLogin(acct)}
                    disabled={!!demoBusy}
                    data-testid={`demo-login-${acct.role}`}
                    className="group flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border border-border bg-card hover:bg-primary/5 hover:border-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={acct.email}
                  >
                    <Icon className={`w-4 h-4 ${isBusy ? "animate-pulse text-primary" : "text-primary/80 group-hover:text-primary"}`} />
                    <span className="text-[11px] font-medium text-foreground leading-tight text-center">
                      {isBusy ? "..." : label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
